import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { Container, Row, Col, Button } from 'react-bootstrap';

import { getFile } from '../../../../util/file.js';
import { getContentBlockMixedItem, getContentBlockMixedItems } from '../../../Cms/CmsActions.js';

import HtmlComponent from '../../../../components/Text/HtmlComponent.js';

// "Savoir-faire" slider: repeatable `expertise` mixed items (titre / texte / image / tags).
// Both the text column and the image column are rendered in full in the DOM (a translated
// track, not conditional mounting) so every slide stays crawlable for SEO.
function HomeExpertise({ items = [] }) {
    const intl = useIntl();
    const [active, setActive] = useState(0);

    const slides = (items || []).filter(item => getContentBlockMixedItem(item, 'title') || getContentBlockMixedItem(item, 'text') || getContentBlockMixedItem(item, 'image'));
    if(!slides.length) {
        return null;
    }

    const total = slides.length;
    const index = ((active % total) + total) % total;
    const go = next => setActive(((next % total) + total) % total);
    const trackStyle = {
        display: 'flex',
        transform: `translateX(-${index * 100}%)`,
        transition: 'transform 320ms cubic-bezier(.22,1,.36,1)',
    };
    const pad = value => `0${value}`.slice(-2);

    return (
        <section className="bg-light border-top">
            <Container className="py-3 py-md-5">
                <Row className=" align-items-center g-4 g-lg-5">
                    <Col xs="12" lg="7" className="position-relative">
						<div className="px-1 px-md-2">
							<span
								aria-hidden="true"
								className="position-absolute bottom-0 end-0 top-lg-0 start-lg-0 fw-bold lh-1 user-select-none"
								style={{ fontSize: '7rem', color: 'rgba(20,25,26,0.06)', transform: 'translateY(-40%)', pointerEvents: 'none' }}
							>
								{pad(index + 1)}
							</span>
							<span className="d-inline-block mb-2 text-uppercase fw-bold text-danger small position-relative" style={{ letterSpacing: '0.1em' }}>
								<FormattedMessage id="homeExpertiseEyebrow" defaultMessage="Our know-how" />
							</span>
							<h2 className="h2 fw-bold text-dark position-relative">
								<FormattedMessage id="homeExpertiseTitle" defaultMessage="We don't mess around with charcuterie!" />
							</h2>

							<div className="overflow-hidden position-relative">
								<div style={trackStyle}>
									{slides.map((slide, slideIndex) => {
										const title = getContentBlockMixedItem(slide, 'title');
										const text = getContentBlockMixedItem(slide, 'text');
										const tags = getContentBlockMixedItems(slide, 'tags');
										return (
											<div
												key={slideIndex}
												className="flex-shrink-0 w-100"
												aria-hidden={slideIndex !== index}
											>
												{title && title.text && <h3 className="h5 fw-bold text-dark mt-3">{title.text}</h3>}
												{text && text.text && <div className="text-body"><HtmlComponent text={text.text} /></div>}
												{tags && tags.length > 0 && (
													<div className="d-flex flex-wrap gap-2 mt-3">
														{tags.filter(tag => tag && tag.text).map((tag, tagIndex) => (
															<span key={tagIndex} className="badge rounded-pill bg-white text-body border fw-semibold">{tag.text}</span>
														))}
													</div>
												)}
											</div>
										);
									})}
								</div>
							</div>
						</div>

                        {total > 1 && (
                            <div className="d-flex justify-content-center align-items-center gap-3 mt-1 mt-md-2 mt-lg-4 position-relative">
                                <Button variant="outline-dark" size="sm" onClick={() => go(index - 1)} aria-label={intl.formatMessage({ id: 'previous', defaultMessage: 'Previous' })} className="rounded-circle">‹</Button>
                                <Button variant="dark" size="sm" onClick={() => go(index + 1)} aria-label={intl.formatMessage({ id: 'next', defaultMessage: 'Next' })} className="rounded-circle">›</Button>
                                <span className="small fw-bold text-muted" style={{ letterSpacing: '0.06em' }}>{pad(index + 1)} / {pad(total)}</span>
                                <div className="d-flex gap-1 ms-1">
                                    {slides.map((slide, slideIndex) => (
                                        <button
                                            key={slideIndex}
                                            type="button"
                                            onClick={() => go(slideIndex)}
                                            aria-label={`${slideIndex + 1}`}
                                            className={classNames('border-0 p-0', slideIndex === index ? 'bg-danger' : 'bg-secondary')}
                                            style={{ width: slideIndex === index ? '22px' : '8px', height: '8px', borderRadius: '999px', cursor: 'pointer', transition: 'width 200ms cubic-bezier(.22,1,.36,1)' }}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </Col>

                    <Col xs="12" lg="5" className="d-none d-lg-block">
                        <div className="overflow-hidden rounded-3 shadow">
                            <div style={trackStyle}>
                                {slides.map((slide, slideIndex) => {
                                    const image = getContentBlockMixedItem(slide, 'image');
                                    const title = getContentBlockMixedItem(slide, 'title');
                                    return (
                                        <div key={slideIndex} className="flex-shrink-0 w-100 bg-primary" style={{ aspectRatio: '16 / 9' }}>
                                            {image && image.path && (
                                                <img
                                                    src={getFile(image.path)}
                                                    alt={(image.text || (title && title.text)) || ''}
                                                    className="w-100 h-100"
                                                    style={{ objectFit: 'cover' }}
                                                    loading="lazy"
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </Col>
                </Row>
            </Container>
        </section>
    );
}

HomeExpertise.defaultProps = {
    items: [],
};

HomeExpertise.propTypes = {
    items: PropTypes.arrayOf(PropTypes.object),
};

export default HomeExpertise;
